import React from "react";
import { Button, Flex, Text, Heading } from "@radix-ui/themes";
import { Search } from "@/components/shared/inputs/search";
import { skillCategories, SkillTemplate } from "./templates";
import styles from "./skills.module.scss";
import { LibraryIcon } from "@/v2/assets/icons/Library";
import { useIntegrations } from "@/api/integration/Integration.api";
import { Skill } from "@/v2/entities/skill/Skill.model";
import { PlusIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { Tooltip } from "@/components/shared/tooltip";
import { Dialog } from "@/components/shared/dialog";

interface SkillsModalContentProps {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  templateSearch: string;
  setTemplateSearch: (search: string) => void;
  selectedTemplate: SkillTemplate | null;
  setSelectedTemplate: (template: SkillTemplate | null) => void;
  filteredTemplates: SkillTemplate[];
  handleTemplateSelect: (template: SkillTemplate) => void;
  skillName: string;
  setSkillName: (name: string) => void;
  slug: string;
  setSlug: (slug: string) => void;
  description: string;
  setDescription: (desc: string) => void;
  curl: string;
  setCurl: (curl: string) => void;
  handleCreateSkill: () => void;
  loading: boolean;
  existingSkills: Skill[];
  handleClose: () => void;
  onlyTemplates?: boolean;
}

export function SkillsModalContent({
  selectedTab,
  setSelectedTab,
  templateSearch,
  setTemplateSearch,
  selectedTemplate,
  setSelectedTemplate,
  filteredTemplates,
  handleTemplateSelect,
  skillName,
  setSkillName,
  slug,
  setSlug,
  description,
  setDescription,
  curl,
  setCurl,
  handleCreateSkill,
  loading,
  existingSkills,
  handleClose,
  onlyTemplates = false,
}: SkillsModalContentProps) {
  const { data: integrations } = useIntegrations();

  const {
    isWikiEnabled,
    isKnowledgeEnabled,
    isTicketEnabled,
    isGDriveEnabled,
    isFreshdeskEnabled,
    isZendeskKnowledgeEnabled,
  } = React.useMemo(() => {
    const wiki = integrations?.some(
      (integration) => integration.type === "WIKI"
    );
    const knowledge = integrations?.some(
      (integration) => integration.type === "KNOWLEDGE_BASE"
    );
    const ticket = integrations?.some(
      (integration) =>
        integration.type === "TICKET_SYSTEM" && integration.name === "zendesk"
    );
    const freshdesk = integrations?.some(
      (integration) =>
        integration.type === "TICKET_SYSTEM" && integration.name === "freshdesk"
    );
    const googleDrive = integrations?.some(
      (integration) => integration.name === "google_drive"
    );
    const zendeskKnowledge = integrations?.some(
      (integration) =>
        integration.type === "KNOWLEDGE_BASE" && integration.name === "zendesk"
    );

    return {
      isWikiEnabled: wiki,
      isKnowledgeEnabled: knowledge,
      isTicketEnabled: ticket,
      isGDriveEnabled: googleDrive,
      isFreshdeskEnabled: freshdesk,
      isZendeskKnowledgeEnabled: zendeskKnowledge,
    };
  }, [integrations]);

  const isSkillAlreadyAdded = (template: SkillTemplate) => {
    return existingSkills?.some((skill) => skill.slug === template.slug);
  };

  return (
    <>
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200">
        <Dialog.Title>Create New Skill</Dialog.Title>
      </div>

      {/* Custom Tab Navigation */}
      {!onlyTemplates && (
        <div className="flex-shrink-0 pt-4">
          <div className={styles.customTabList}>
            <button
              className={`
                flex gap-2
                ${styles.customTabTrigger} ${
                selectedTab === "templates" ? styles.customTabTriggerActive : ""
              }`}
              onClick={() => setSelectedTab("templates")}
            >
              <LibraryIcon size={16} />
              Library
            </button>
            <button
              className={`${styles.customTabTrigger} ${
                selectedTab === "custom" ? styles.customTabTriggerActive : ""
              }`}
              onClick={() => setSelectedTab("custom")}
            >
              <PlusIcon className="mr-2" />
              Custom
            </button>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div
        className="pt-2 flex-1 flex flex-col overflow-hidden"
        style={{ minHeight: 0 }}
      >
        {/* Library Tab Content */}
        {selectedTab === "templates" && (
          <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
            <div className="flex-shrink-0 pb-3 pl-4 border-b border-gray-100">
              <Search
                initialValue={templateSearch}
                placeholder="Search built-in skills..."
                setSearchPhrase={setTemplateSearch}
                debounce={200}
              />
            </div>

            {/* Selected template indicator */}
            {selectedTemplate && (
              <div className="flex-shrink-0 p-3 bg-blue-50 border border-blue-200 rounded-md m-4 mb-2">
                <Flex align="center" justify="between">
                  <Flex align="center" gap="2">
                    <span className="text-lg">{selectedTemplate.icon}</span>
                    <Text size="2" weight="medium">
                      Selected: {selectedTemplate.name}
                    </Text>
                  </Flex>
                  <Button
                    variant="soft"
                    color="blue"
                    highContrast
                    size="1"
                    onClick={() => setSelectedTemplate(null)}
                  >
                    Clear
                  </Button>
                </Flex>
              </div>
            )}

            <div
              className={`flex-1 ${styles.skillsModalScrollArea}`}
              style={{ minHeight: 0 }}
            >
              <div className="space-y-6 ">
                {filteredTemplates.length === 0 && templateSearch ? (
                  <div className="text-center py-12 gap-2 flex items-center justify-center ">
                    <Text size="3" color="gray">
                      No built-in skills found for "{templateSearch}"
                    </Text>
                    <Button
                      variant="soft"
                      color="gray"
                      onClick={() => setTemplateSearch("")}
                    >
                      Show all
                    </Button>
                  </div>
                ) : (
                  skillCategories
                    .filter((category) => {
                      if (category === "Wiki") {
                        return isWikiEnabled;
                      }
                      if (category === "Knowledge") {
                        return isKnowledgeEnabled;
                      }
                      if (category === "Ticket Management") {
                        return isTicketEnabled;
                      }
                      if (category === "Google drive") {
                        return isGDriveEnabled;
                      }
                      if (category === "Ticket Management - Freshdesk") {
                        return isFreshdeskEnabled;
                      }
                      return true;
                    })
                    .map((category) => {
                      const templatesInCategory = filteredTemplates.filter(
                        (template) => {
                          if (template.category !== category) return false;
                          if (
                            template.slug === "search_article_language" &&
                            !isZendeskKnowledgeEnabled
                          ) {
                            return false;
                          }
                          return true;
                        }
                      );

                      if (templatesInCategory.length === 0) return null;

                      return (
                        <div key={category}>
                          <div
                            className={`sticky top-0 py-2 z-10 mb-2 ${styles.stickyHeader}`}
                          >
                            <Heading
                              size="3"
                              color="gray"
                              className="mb-0 pl-4"
                            >
                              {category}
                            </Heading>
                          </div>
                          <div className={styles.templateGrid}>
                            {templatesInCategory.map((template) => {
                              const isAlreadyAdded =
                                isSkillAlreadyAdded(template);
                              return (
                                <Button
                                  key={template.slug}
                                  variant={"ghost"}
                                  color={
                                    selectedTemplate?.slug === template.slug
                                      ? "blue"
                                      : "gray"
                                  }
                                  size="3"
                                  className={`text-left flex justify-start cursor-pointer p-4 border rounded-md transition-all ${
                                    selectedTemplate?.slug === template.slug
                                      ? "border-blue-300 bg-blue-50"
                                      : isAlreadyAdded
                                      ? "border-green-200 bg-green-50"
                                      : "border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                                  } ${styles.skillTemplateCard}`}
                                  onClick={() => handleTemplateSelect(template)}
                                  disabled={isAlreadyAdded}
                                >
                                  <Flex align="center" gap="3" width="100%">
                                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-gray-50 rounded-md">
                                      <span className="text-lg">
                                        {isAlreadyAdded ? "✅" : template.icon}
                                      </span>
                                    </div>
                                    <Flex
                                      direction="column"
                                      align="start"
                                      gap="1"
                                      className="flex-1 min-w-0"
                                    >
                                      <Flex align="center" gap="2" width="100%">
                                        <Text
                                          weight="medium"
                                          size="2"
                                          className="truncate flex-1"
                                        >
                                          {template.name}
                                        </Text>
                                        {template.helpText && (
                                          <Tooltip>
                                            <Tooltip.Trigger>
                                              <InfoCircledIcon className="text-gray-400 hover:text-gray-600 cursor-help" />
                                            </Tooltip.Trigger>
                                            <Tooltip.Content>
                                              <div className="max-w-xs p-2">
                                                <Text
                                                  size="2"
                                                  weight="medium"
                                                  mb="1"
                                                >
                                                  {template.helpText}
                                                </Text>
                                                <br />
                                                {template.example && (
                                                  <Text
                                                    size="1"
                                                    color="gray"
                                                    mt="1"
                                                  >
                                                    Example: {template.example}
                                                  </Text>
                                                )}
                                              </div>
                                            </Tooltip.Content>
                                          </Tooltip>
                                        )}
                                        {isAlreadyAdded && (
                                          <Text
                                            size="1"
                                            color="green"
                                            weight="medium"
                                          >
                                            Added
                                          </Text>
                                        )}
                                      </Flex>
                                      <Text
                                        size="1"
                                        color="gray"
                                        className="line-clamp-2 text-left"
                                      >
                                        {template.description}
                                      </Text>
                                    </Flex>
                                  </Flex>
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          </div>
        )}

        {/* Custom Tab Content */}
        {selectedTab === "custom" && (
          <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
            <div
              className={`flex-1 ${styles.skillsModalScrollArea}`}
              style={{ minHeight: 0 }}
            >
              <div className="p-4 space-y-4">
                {selectedTemplate && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md mb-4">
                    <Flex align="center" gap="2">
                      <span className="text-lg">{selectedTemplate.icon}</span>
                      <Text size="2" weight="medium">
                        Using built-in skill: {selectedTemplate.name}
                      </Text>
                    </Flex>
                  </div>
                )}

                <div className="space-y-4 gap-2 flex flex-col">
                  <label>
                    <Text as="div" size="2" mb="1" weight="bold">
                      Name
                    </Text>
                    <input
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-200"
                      placeholder="Enter skill name"
                      value={skillName}
                      onChange={(e) => {
                        setSkillName(e.target.value);
                        setSlug(e.target.value);
                      }}
                    />
                  </label>

                  <label>
                    <Text as="div" size="2" mb="1" weight="bold">
                      Slug
                    </Text>
                    <input
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-200"
                      placeholder="Enter skill slug"
                      value={slug.toLowerCase().replace(/ /g, "_")}
                      onChange={(e) => setSlug(e.target.value)}
                    />
                  </label>

                  <label>
                    <Text as="div" size="2" mb="1" weight="bold">
                      Description
                    </Text>
                    <textarea
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none"
                      placeholder="Enter skill description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                    />
                  </label>

                  <label>
                    <Text as="div" size="2" mb="1" weight="bold">
                      CURL Command
                    </Text>
                    <textarea
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-200 font-mono text-sm resize-none"
                      placeholder="Enter CURL command starting with 'curl'"
                      rows={8}
                      value={curl}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          value.trim() === "" ||
                          value.trim().toLowerCase().startsWith("curl")
                        ) {
                          setCurl(value);
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Always visible bottom action buttons */}
      <div className={`flex-shrink-0 p-4 ${styles.floatingActions}`}>
        <Flex gap="3" justify="end">
          <Button variant="soft" color="gray" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="gray"
            highContrast
            onClick={handleCreateSkill}
            loading={loading}
            disabled={!skillName.trim() || !description.trim() || !curl.trim()}
          >
            Create Skill
          </Button>
        </Flex>
      </div>
    </>
  );
}
